import React from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import type Hotel from '@this/domain/hotel/hotel';
import type HotelList from '@this/domain/hotel/hotel_list';
import type SelectRepository from '@this/domain/select_repository';
import type SearchQueryItem from '@this/domain/search_query_item';
import type User from '@this/domain/user/user';
import type { HotelBoxProps } from '@this/components/reserve_trip/select/hotel_box/types';
import DestMarker from './dest_marker';
import HotelMarker from './hotel_marker';
import HotelIconMarker from './hotel_icon_marker';
import HotelDetailOnMap from './hotel_detail_on_map';

interface Props {
  height?: string;
  destLocation?: { lat: number; lng: number } | null;
  hotel?: Hotel | null;
  hotels?: HotelList;
  serviceId?: number;
  hotelPriceLimit?: number | null;
  searchQueryId: number | null;
  onHotelSelect?: (i: number) => void;
  onChange?: () => void;
  repository?: SelectRepository;
  queryItem?: SearchQueryItem;
  me?: User | null;
  user?: HotelBoxProps['user'];
}

interface State {
  visibleDetail: boolean;
  detailTargetHotel: Hotel | null;
  isFullscreen: boolean;
}

type DocumentWithFullscreen = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
};

type ElementWithFullscreen = HTMLElement & {
  webkitRequestFullscreen?: () => void;
};

const getDocumentFullscreenElement = (): HTMLElement | null => {
  const doc = document as DocumentWithFullscreen;
  const el = doc.fullscreenElement || doc.webkitFullscreenElement || null;
  return el instanceof HTMLElement ? el : null;
};

const exitDocumentFullscreen = (): Promise<void> => {
  const doc = document as DocumentWithFullscreen;
  if (doc.exitFullscreen) {
    return doc.exitFullscreen().catch(() => undefined);
  }
  if (doc.webkitExitFullscreen) {
    doc.webkitExitFullscreen();
  }
  return Promise.resolve();
};

const requestElementFullscreen = (el: HTMLElement): Promise<void> => {
  const target = el as ElementWithFullscreen;
  if (target.requestFullscreen) {
    return target.requestFullscreen().catch(() => undefined);
  }
  if (target.webkitRequestFullscreen) {
    target.webkitRequestFullscreen();
  }
  return Promise.resolve();
};

class HotelMap extends React.Component<Props, State> {
  map: google.maps.Map | null;

  ref: React.RefObject<HTMLDivElement>;

  wrapperRef: React.RefObject<HTMLDivElement>;

  destMarker: DestMarker | null;

  hotelIconMarker: HotelIconMarker | null;

  boundsAdjusted: boolean;

  markers: { [key: string]: HotelMarker };

  normalMapSize: { width: number; height: number } | null;

  fullscreenButton: HTMLButtonElement | null;

  constructor(props: Props) {
    super(props);
    this.map = null;
    this.ref = React.createRef<HTMLDivElement>();
    this.wrapperRef = React.createRef<HTMLDivElement>();
    this.destMarker = null;
    this.hotelIconMarker = null;
    this.boundsAdjusted = false;
    this.markers = {};
    this.normalMapSize = null;
    this.fullscreenButton = null;
    this.state = {
      visibleDetail: false,
      detailTargetHotel: null,
      isFullscreen: false
    };
  }

  componentDidMount() {
    this.map = this.createMap();
    this.updateMap();
    this.addMapControls();
    this.captureNormalMapSize();
    document.addEventListener('fullscreenchange', this.syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', this.syncFullscreenState);
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    this.updateMap();
    if (prevState.isFullscreen !== this.state.isFullscreen && this.map) {
      google.maps.event.trigger(this.map, 'resize');
    }
  }

  componentWillUnmount() {
    document.removeEventListener('fullscreenchange', this.syncFullscreenState);
    document.removeEventListener('webkitfullscreenchange', this.syncFullscreenState);
  }

  createMap() {
    if (google && this.ref.current) {
      return new google.maps.Map(this.ref.current, {
        zoom: 14,
        center: new google.maps.LatLng(35.681247, 139.766709),
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        // Google標準の全画面は地図divだけが対象になり、詳細パネルのスタイルが壊れる
        fullscreenControl: false
      });
    }
    return null;
  }

  getMapDiv(): HTMLElement | null {
    const mapDiv = this.map?.getDiv() ?? this.ref.current;
    return mapDiv instanceof HTMLElement ? mapDiv : null;
  }

  isWrapperFullscreen(): boolean {
    const wrapper = this.wrapperRef.current;
    const fullscreenEl = getDocumentFullscreenElement();
    return !!fullscreenEl && !!wrapper && (fullscreenEl === wrapper || wrapper.contains(fullscreenEl));
  }

  captureNormalMapSize() {
    if (this.isWrapperFullscreen()) {
      return;
    }
    const mapDiv = this.getMapDiv();
    if (!mapDiv) {
      return;
    }
    this.normalMapSize = { width: mapDiv.clientWidth, height: mapDiv.clientHeight };
  }

  syncFullscreenState = () => {
    const isFullscreen = this.isWrapperFullscreen();
    if (!isFullscreen) {
      this.captureNormalMapSize();
    }
    if (this.state.isFullscreen !== isFullscreen) {
      this.setState({ isFullscreen });
    }
    this.updateFullscreenButtonLabel();
  };

  addMapControls() {
    const controlDiv = document.createElement('div');
    controlDiv.style.display = 'flex';
    controlDiv.style.flexDirection = 'row';
    controlDiv.style.alignItems = 'flex-start';
    controlDiv.style.gap = '10px';
    controlDiv.style.margin = '10px';

    if (this.props.repository) {
      controlDiv.appendChild(this.createAreaSearchButton());
    }
    controlDiv.appendChild(this.createFullscreenButton());

    this.updateFullscreenButtonLabel();
    this.map?.controls[google.maps.ControlPosition.TOP_RIGHT].push(controlDiv);
  }

  createFullscreenButton() {
    const controlButton = document.createElement('button');
    controlButton.style.backgroundColor = '#fff';
    controlButton.style.border = '0';
    controlButton.style.borderRadius = '2px';
    controlButton.style.boxShadow = '0 1px 4px rgba(0,0,0,.3)';
    controlButton.style.width = '40px';
    controlButton.style.height = '40px';
    controlButton.style.padding = '0';
    controlButton.style.cursor = 'pointer';
    controlButton.style.display = 'flex';
    controlButton.style.alignItems = 'center';
    controlButton.style.justifyContent = 'center';
    controlButton.style.flexShrink = '0';
    controlButton.type = 'button';
    controlButton.addEventListener('click', () => this.toggleFullscreen());
    this.fullscreenButton = controlButton;
    return controlButton;
  }

  updateFullscreenButtonLabel() {
    if (!this.fullscreenButton) {
      return;
    }
    const isFullscreen = this.isWrapperFullscreen();
    this.fullscreenButton.title = isFullscreen ? '全画面を閉じる' : '全画面表示';
    this.fullscreenButton.setAttribute('aria-label', this.fullscreenButton.title);
    this.fullscreenButton.innerHTML = isFullscreen
      ? '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18"><path fill="#666" d="M4 10H0v2h4v4h2v-6H4zm10 6h2v-4h4v-2h-6v6zM4 2v4H0v2h6V2H4zm10 4V2h-2v6h6V6h-4z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18"><path fill="#666" d="M0 0v6h2V2h4V0H0zm16 0h-6v2h4v4h2V0zM2 16v-4H0v6h6v-2H2zm14-4v4h-4v2h6v-6h-2z"/></svg>';
  }

  toggleFullscreen() {
    if (this.isWrapperFullscreen()) {
      exitDocumentFullscreen();
      return;
    }
    const wrapper = this.wrapperRef.current;
    if (wrapper) {
      this.captureNormalMapSize();
      requestElementFullscreen(wrapper);
    }
  }

  updateMap() {
    this.reloadMarkers();
    if (this.hotelIconMarker && !this.boundsAdjusted && this.map) {
      this.map.setCenter(this.hotelIconMarker.location);
    } else if (this.props.destLocation && !this.boundsAdjusted && this.map) {
      this.map.setCenter(this.props.destLocation);
    }
    this.adjustBounds();
  }

  createAreaSearchButton() {
    const controlButton = document.createElement('button');
    controlButton.style.backgroundColor = '#fff';
    controlButton.style.border = '0';
    controlButton.style.borderRadius = '2px';
    controlButton.style.boxShadow = '0 1px 4px rgba(0,0,0,.3)';
    controlButton.style.color = 'rgb(25,25,25)';
    controlButton.style.cursor = 'pointer';
    controlButton.style.fontFamily = 'Roboto,Arial,sans-serif';
    controlButton.style.fontSize = '14px';
    controlButton.style.lineHeight = '40px';
    controlButton.style.height = '40px';
    controlButton.style.padding = '0 10px';
    controlButton.style.textAlign = 'center';
    controlButton.textContent = 'このエリアを検索';
    controlButton.title = 'このエリアを検索';
    controlButton.type = 'button';
    controlButton.addEventListener('click', () => {
      if (this.map != null && this.props.queryItem) {
        const latlng = this.map.getCenter();
        this.props.repository?.reSearchHotel(latlng.lat(), latlng.lng(), this.props.queryItem);
        this.map.setCenter(latlng);
      }
    });
    return controlButton;
  }

  reloadMarkers() {
    const { destLocation, hotels, serviceId } = this.props;
    if (!google) {
      return;
    }
    if (!this.destMarker && destLocation) {
      this.destMarker = new DestMarker(destLocation, serviceId || 1);
    }
    if (this.destMarker) {
      this.destMarker.setMap(this.map);
    }
    if (!this.hotelIconMarker && this.props.hotel) {
      if (this.props.hotel.latitude && this.props.hotel.longitude) {
        this.hotelIconMarker = new HotelIconMarker({
          lat: this.props.hotel.latitude,
          lng: this.props.hotel.longitude
        });
      }
    }
    if (this.hotelIconMarker) {
      this.hotelIconMarker.setMap(this.map);
    }
    if (typeof hotels === 'undefined') {
      return;
    }

    const hotelList = hotels.filteredList;
    const inStockHotels = hotelList.filter(h => !h.sold_out).filter(h => h.latitude && h.longitude);
    const newMarkers: { [key: string]: HotelMarker } = {};
    _.each(inStockHotels, h => {
      if (!h.id) {
        return;
      }
      const m =
        this.markers[h.id] ||
        new HotelMarker(h, hotels, serviceId || 1, hotel => this.handleClickHotelMarker(hotel));
      m.selected = hotels.currentId === h.id;
      m.showDetail = this.state.detailTargetHotel?.id === h.id;
      m.setMap(this.map);
      newMarkers[h.id] = m;
      this.markers[h.id] = m;
    });

    _.each(this.markers, (marker, hotelId) => {
      if (!newMarkers[hotelId]) {
        marker.setMap(null);
      }
    });

    this.markers = newMarkers;
  }

  adjustBounds() {
    if (this.boundsAdjusted || !this.map || this.hotelIconMarker) {
      return;
    }
    this.boundsAdjusted = true;
    let centerLat = 0;
    let centerLng = 0;
    let count = 0;

    const topMarkers = _.values(this.markers).slice(0, 5);
    _.each(topMarkers, m => {
      if (m.location) {
        centerLat += m.location.lat();
        centerLng += m.location.lng();
        count += 1;
      }
    });

    if (this.props.destLocation) {
      centerLat += this.props.destLocation.lat;
      centerLng += this.props.destLocation.lng;
      count += 1;
    }
    const centerLatLng = new google.maps.LatLng(centerLat / count, centerLng / count);
    this.map.setCenter(centerLatLng);

    let i = 0;
    const mapEventListener = google.maps.event.addListener(this.map, 'bounds_changed', () => {
      if (this.map) {
        this.boundsAdjusted = true;
        const bounds = this.map.getBounds();
        if (!bounds) {
          return;
        }
        const m = topMarkers[i];
        if (!m) {
          return;
        }
        const location = m.location;
        if (!location) {
          return;
        }
        if (this.destMarker && this.destMarker.location) {
          if (!bounds.contains(this.destMarker.location)) {
            this.map.setZoom(this.map.getZoom() - 1);
            return;
          }
        }
        if (bounds.contains(location)) {
          i += 1;
          if (i >= topMarkers.length) {
            google.maps.event.removeListener(mapEventListener);
          } else {
            google.maps.event.trigger(this.map, 'bounds_changed');
          }
        } else {
          this.map.setZoom(this.map.getZoom() - 1);
        }
      }
    });
  }

  handleClickHotelMarker(hotel: Hotel) {
    if (hotel) {
      this.setState({ visibleDetail: true });
      if (this.state.detailTargetHotel !== hotel) {
        this.setState({ detailTargetHotel: hotel });
      }
    }
  }

  handleCloseHotelDetail() {
    this.setState({ visibleDetail: false, detailTargetHotel: null });
    this.reloadMarkers();
  }

  applyHotelSelect(hotel: Hotel) {
    if (this.props.onChange) {
      this.props.onChange();
    }
    this.props.hotels?.recordItemSelect(hotel);
    if (!_.isNil(hotel.orderedIndex) && this.props.onHotelSelect) {
      this.props.onHotelSelect(hotel.orderedIndex);
    }
    this.setState({ visibleDetail: false, detailTargetHotel: null });
    this.reloadMarkers();
  }

  // 全画面中に詳細パネルを外すと画面が固まるため、先に全画面を解除する
  handleHotelSelect(hotel: Hotel) {
    if (!this.isWrapperFullscreen()) {
      this.applyHotelSelect(hotel);
      return;
    }

    let finished = false;
    const finish = () => {
      if (finished) {
        return;
      }
      finished = true;
      document.removeEventListener('fullscreenchange', finish);
      document.removeEventListener('webkitfullscreenchange', finish);
      this.applyHotelSelect(hotel);
    };

    document.addEventListener('fullscreenchange', finish);
    document.addEventListener('webkitfullscreenchange', finish);
    exitDocumentFullscreen().then(() => {
      window.setTimeout(finish, 0);
    });
  }

  render() {
    const { height, destLocation, serviceId, me, user } = this.props;
    const { isFullscreen } = this.state;
    const overlayWidth = isFullscreen && this.normalMapSize ? this.normalMapSize.width - 58 : undefined;

    return (
      <Wrapper ref={this.wrapperRef} $isFullscreen={isFullscreen}>
        <Map height={isFullscreen ? '100%' : height} ref={this.ref} $isFullscreen={isFullscreen} />
        {this.state.detailTargetHotel && this.props.hotels ? (
          <Overlay data-testid="hotel-map-overlay" data-overlay-width={overlayWidth} $width={overlayWidth}>
            <HotelDetailOnMap
              destLocation={destLocation}
              me={me}
              user={user}
              hotel={this.state.detailTargetHotel}
              hotels={this.props.hotels}
              hotelPriceLimit={this.props.hotelPriceLimit || null}
              searchQueryId={this.props.searchQueryId}
              serviceId={serviceId}
              selected={this.props.hotels.currentId === this.state.detailTargetHotel.id}
              visible={this.state.visibleDetail}
              onClose={() => this.handleCloseHotelDetail()}
              handleHotelSelect={hotel => this.handleHotelSelect(hotel)}
            />
          </Overlay>
        ) : null}
      </Wrapper>
    );
  }
}

const Wrapper = styled.div<{ $isFullscreen: boolean }>`
  position: relative;
  width: 100%;
  ${props =>
    props.$isFullscreen
      ? `
    height: 100%;
    background: #e5e3df;
  `
      : ''}
`;

const Overlay = styled.div<{ $width?: number }>`
  position: absolute;
  left: 0;
  bottom: 0;
  width: ${props => (props.$width ? `${props.$width}px` : 'calc(100% - 58px)')};
  height: fit-content;
  margin: 4px;
  z-index: 2;
  pointer-events: auto;
`;

const Map = styled.div<{ height?: string; $isFullscreen: boolean }>`
  position: relative;
  flex-grow: 99999;
  flex-basis: 300px;
  height: ${props => (props.$isFullscreen ? '100%' : props.height || '300px')};
`;

export default HotelMap;
